import { DateTimeAgo } from '@app/components/date/date-time-ago/DateTimeAgo';
import { ListViewChildProps } from '@app/core/list-view/ListView';
import { toDateTimeStr } from '@mi-front/core';
import { MrtTable } from '@mi-front/ui';
import { Box } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { getAppUserDisplayName } from '../../../../../../../helpers/user';
import { SavedReportListItem } from '../types';
import { ReportListStatus } from './ReportListStatus';
import { SavedReportsStationCell } from './SavedReportsStationCell';

const BROADCAST_EVIDENCE_TYPE = 'AUDIT_BROADCAST_EVIDENCE';

interface SavedReportsTableProps extends ListViewChildProps<SavedReportListItem> {
  /** When true, shows the creator column (GIP / GIP alt). */
  showCreatedBy?: boolean;
  /** When true, shows export/generation status (GIP / GIP alt). */
  showExportStatus?: boolean;
  /** When true, shows the station column (broadcast evidence). */
  showStation?: boolean;
}

/**
 * Returns the detail path for a saved report row based on its type.
 */
function getSavedReportPath(item: SavedReportListItem): string {
  if (item.type === BROADCAST_EVIDENCE_TYPE) {
    return `/audit/saved-reports/broadcast-evidence/${item.id}`;
  }
  return `/audit/saved-reports/${item.id}`;
}

/**
 * MRT table for saved reports ListView (filters live in the DataToolbar).
 */
export function SavedReportsTable({
  data,
  total,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  showCreatedBy = false,
  showExportStatus = false,
  showStation = false,
}: SavedReportsTableProps) {
  const { formatMessage } = useIntl();

  return (
    <Box>
      <MrtTable<SavedReportListItem>
        data={data}
        rowCount={total}
        state={{
          pagination,
          sorting,
        }}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        manualPagination
        manualSorting
        enableColumnFilters={false}
        enableToolbarInternalActions={false}
        enableGlobalFilter={false}
        paginationDisplayMode="default"
        navigateToOnClick={(_id, row) => getSavedReportPath(row)}
        muiTopToolbarProps={{
          sx: {
            height: '4px !important',
            minHeight: '4px !important',
          },
        }}
        columns={[
          {
            id: 'createdAt',
            header: formatMessage({
              id: 'audit.report.created',
              defaultMessage: 'Creado',
            }),
            accessorFn: (row) => row.createdAt,
            Cell: ({ row: { original } }) => (
              <DateTimeAgo value={toDateTimeStr(original.createdAt)} />
            ),
            size: 180,
            grow: false,
            enableSorting: true,
          },
          ...(showStation
            ? [
                {
                  id: 'station',
                  header: formatMessage({
                    id: 'audit.broadcastEvidence.station',
                    defaultMessage: 'Emisora',
                  }),
                  accessorFn: (row: SavedReportListItem) => row.stationId,
                  Cell: ({ row: { original } }: { row: { original: SavedReportListItem } }) =>
                    original.stationId ? (
                      <SavedReportsStationCell stationId={original.stationId} />
                    ) : null,
                  size: 220,
                  grow: false,
                  enableSorting: false,
                },
              ]
            : []),
          {
            id: 'name',
            header: formatMessage({
              id: 'audit.report.name',
              defaultMessage: 'Nombre',
            }),
            accessorFn: (row) => row.name,
            Cell: ({ row: { original } }) => original.name,
            size: 150,
            enableSorting: true,
          },
          ...(showCreatedBy
            ? [
                {
                  id: 'createdBy',
                  header: formatMessage({
                    id: 'audit.reportsList.createdBy',
                    defaultMessage: 'Creado por',
                  }),
                  accessorFn: (row: SavedReportListItem) => row.createdBy ?? row.createdByUserId,
                  Cell: ({ row: { original } }: { row: { original: SavedReportListItem } }) => {
                    if (original.createdBy) {
                      return (
                        <div className="ml-1 text-gray-600">
                          {getAppUserDisplayName(original.createdBy)}
                        </div>
                      );
                    }
                    if (original.createdByUserId) {
                      return <div className="ml-1 text-gray-600">{original.createdByUserId}</div>;
                    }
                    return (
                      <FormattedMessage
                        id="audit.reportsView.createdByUnknown"
                        defaultMessage="Sin datos"
                      >
                        {(txt) => <span className="text-muted">{txt}</span>}
                      </FormattedMessage>
                    );
                  },
                  size: 100,
                },
              ]
            : []),
          ...(showExportStatus
            ? [
                {
                  id: 'exportStatus',
                  header: formatMessage({
                    id: 'audit.reportsList.exportStatus',
                    defaultMessage: 'Status de export',
                  }),
                  Cell: ({ row: { original } }: { row: { original: SavedReportListItem } }) => (
                    <ReportListStatus item={original} />
                  ),
                  size: 100,
                },
              ]
            : []),
        ]}
      />
    </Box>
  );
}
